import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import CostFormContent from './cost/CostFormContent';
import { 
  DollarSign, 
  Plus, 
  Edit3, 
  Trash2, 
  TrendingUp,
  Building2,
  PieChart,
  CreditCard,
  FileText,
  Target,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostManagementV2 = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext, displayCurrency, convertCurrency } = useCurrency();
  
  // State Management
  const [activeTab, setActiveTab] = useState('core-costs');
  const [loading, setLoading] = useState(true);
  const [costItems, setCostItems] = useState([]);
  const [costLibraries, setCostLibraries] = useState({});
  const [sfHierarchy, setSfHierarchy] = useState([]);
  const [financialLoans, setFinancialLoans] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Dialog States
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('fixed');
  
  // Form State
  const [costForm, setCostForm] = useState({
    item_name: '',
    cost_category: 'fixed',
    amount: '',
    cost_type: 'monthly',
    description: '',
    
    // Growth
    annual_growth_rate: '',
    growth_rates_per_year: {},
    use_per_year_rates: false,
    
    // SF Linking
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    linked_product_id: '', // Single product link from SF cascade
    
    // Semi-Variable
    fixed_component: '',
    variable_rate: '',
    fixed_portion: '',
    variable_basis: '',
    
    // Direct
    linked_product_ids: [],
    cost_type_detail: 'material',
    unit_cost: '',
    quantity_per_unit: '',
    
    // Indirect
    allocation_method: '',
    allocation_base: '',
    shared_across_products: true,
    
    // OpEx
    department: '',
    approval_required: false,
    budget_allocation: '',
    is_recurring: true,
    
    // CapEx
    asset_name: '',
    useful_life_years: '',
    depreciation_method: 'straight_line',
    salvage_value: '',
    
    // Financial
    loan_reference_id: '',
    loan_name: '',
    interest_rate: '',
    
    // Tax
    tax_type: '',
    tax_rate: '',
    tax_base: 'revenue',
    is_deductible: false,
    
    // Common
    notes: '',
    owner: '',
    start_year: 2025
  });

  // Fetch data on mount
  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan?.id]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [costItemsRes, librariesRes, hierarchyRes, loansRes, productsRes] = await Promise.all([
        axios.get(`${API}/v3/cost-items/${currentPlan.id}`),
        axios.get(`${API}/v3/cost-management/item-library`),
        axios.get(`${API}/v3/cost-management/sf-hierarchy/${currentPlan.id}`),
        axios.get(`${API}/v3/cost-management/financial-loan-sync/${currentPlan.id}`),
        axios.get(`${API}/v2/products-enhanced/${currentPlan.id}`)
      ]);
      
      setCostItems(costItemsRes.data || []);
      setCostLibraries(librariesRes.data?.all_libraries || {});
      setSfHierarchy(hierarchyRes.data?.hierarchy || []);
      setFinancialLoans(loansRes.data?.loans || []);
      setProducts(productsRes.data || []);
      
      console.log('✅ All cost management data loaded');
    } catch (error) {
      console.error('Error loading cost management data:', error);
      toast.error('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  };

  const openCostDialog = (category) => {
    setSelectedCategory(category);
    resetForm(category);
    setEditingCost(null);
    setShowCostDialog(true);
  };

  const handleEditCost = (cost) => {
    setEditingCost(cost);
    setSelectedCategory(cost.cost_category);
    setCostForm({
      item_name: cost.item_name || '',
      cost_category: cost.cost_category || '',
      amount: cost.amount?.toString() || '',
      currency: cost.currency || currentPlan?.currency || 'USD', // Add currency
      cost_type: cost.cost_type || 'monthly',
      description: cost.description || '',
      
      annual_growth_rate: cost.annual_growth_rate?.toString() || '',
      growth_rates_per_year: cost.growth_rates_per_year || {},
      use_per_year_rates: cost.use_per_year_rates || false,
      
      linked_objective_id: cost.linked_objective_id || '',
      linked_initiative_id: cost.linked_initiative_id || '',
      linked_revenue_stream_id: cost.linked_revenue_stream_id || '',
      linked_product_id: cost.linked_product_id || '',
      
      fixed_component: cost.fixed_component?.toString() || '',
      variable_rate: cost.variable_rate?.toString() || '',
      fixed_portion: cost.fixed_portion?.toString() || '',
      variable_basis: cost.variable_basis || '',
      
      linked_product_ids: cost.linked_product_ids || [],
      cost_type_detail: cost.cost_type_detail || 'material',
      unit_cost: cost.unit_cost?.toString() || '',
      quantity_per_unit: cost.quantity_per_unit?.toString() || '',
      
      allocation_method: cost.allocation_method || '',
      allocation_base: cost.allocation_base?.toString() || '',
      shared_across_products: cost.shared_across_products !== false,
      
      department: cost.department || '',
      approval_required: cost.approval_required || false,
      budget_allocation: cost.budget_allocation?.toString() || '',
      is_recurring: cost.is_recurring !== false,
      
      asset_name: cost.asset_name || '',
      useful_life_years: cost.useful_life_years?.toString() || '',
      depreciation_method: cost.depreciation_method || 'straight_line',
      salvage_value: cost.salvage_value?.toString() || '',
      
      loan_reference_id: cost.loan_reference_id || '',
      loan_name: cost.loan_name || '',
      interest_rate: cost.interest_rate?.toString() || '',
      
      tax_type: cost.tax_type || '',
      tax_rate: cost.tax_rate?.toString() || '',
      tax_base: cost.tax_base || 'revenue',
      is_deductible: cost.is_deductible || false,
      
      notes: cost.notes || '',
      owner: cost.owner || '',
      start_year: cost.start_year || 2025
    });
    setShowCostDialog(true);
  };

  const handleSaveCost = async (e) => {
    e.preventDefault();
    
    if (!costForm.item_name.trim() || !costForm.amount) {
      toast.error('Please fill in required fields: Item Name and Amount');
      return;
    }

    try {
      const costData = {
        plan_id: currentPlan.id,
        ...costForm,
        amount: parseFloat(costForm.amount),
        annual_growth_rate: costForm.annual_growth_rate ? parseFloat(costForm.annual_growth_rate) : null,
        fixed_component: costForm.fixed_component ? parseFloat(costForm.fixed_component) : null,
        variable_rate: costForm.variable_rate ? parseFloat(costForm.variable_rate) : null,
        fixed_portion: costForm.fixed_portion ? parseFloat(costForm.fixed_portion) : null,
        unit_cost: costForm.unit_cost ? parseFloat(costForm.unit_cost) : null,
        quantity_per_unit: costForm.quantity_per_unit ? parseFloat(costForm.quantity_per_unit) : null,
        allocation_base: costForm.allocation_base ? parseFloat(costForm.allocation_base) : null,
        budget_allocation: costForm.budget_allocation ? parseFloat(costForm.budget_allocation) : null,
        useful_life_years: costForm.useful_life_years ? parseInt(costForm.useful_life_years) : null,
        salvage_value: costForm.salvage_value ? parseFloat(costForm.salvage_value) : null,
        interest_rate: costForm.interest_rate ? parseFloat(costForm.interest_rate) : null,
        tax_rate: costForm.tax_rate ? parseFloat(costForm.tax_rate) : null
      };

      if (editingCost) {
        await axios.put(`${API}/v3/cost-items/${editingCost.id}`, costData);
        toast.success('Cost item updated successfully!');
      } else {
        await axios.post(`${API}/v3/cost-items`, costData);
        toast.success('Cost item created successfully!');
      }

      setShowCostDialog(false);
      fetchAllData();
      resetForm(selectedCategory);
    } catch (error) {
      console.error('Error saving cost:', error);
      toast.error(error.response?.data?.detail || 'Failed to save cost item');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this cost item?')) return;

    try {
      await axios.delete(`${API}/v3/cost-items/${costId}`);
      toast.success('Cost item deleted successfully!');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting cost:', error);
      toast.error('Failed to delete cost item');
    }
  };

  const resetForm = (category) => {
    setCostForm({
      item_name: '',
      cost_category: category || 'fixed',
      amount: '',
      currency: displayCurrency || currentPlan?.currency || 'USD', // Use display currency first
      cost_type: 'monthly',
      description: '',
      annual_growth_rate: '',
      growth_rates_per_year: {},
      use_per_year_rates: false,
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: '',
      linked_product_id: '',
      fixed_component: '',
      variable_rate: '',
      fixed_portion: '',
      variable_basis: '',
      linked_product_ids: [],
      cost_type_detail: 'material',
      unit_cost: '',
      quantity_per_unit: '',
      allocation_method: '',
      allocation_base: '',
      shared_across_products: true,
      department: '',
      approval_required: false,
      budget_allocation: '',
      is_recurring: true,
      asset_name: '',
      useful_life_years: '',
      depreciation_method: 'straight_line',
      salvage_value: '',
      loan_reference_id: '',
      loan_name: '',
      interest_rate: '',
      tax_type: '',
      tax_rate: '',
      tax_base: 'revenue',
      is_deductible: false,
      notes: '',
      owner: '',
      start_year: 2025
    });
  };

  const formatCurrency = (amount, sourceCurrency = null) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    const fromCurrency = sourceCurrency || currentPlan?.currency || 'USD';
    return formatCurrencyContext(amount, fromCurrency, null, false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cost management...</p>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Business Plan Selected</h3>
            <p className="text-gray-600 text-sm">
              Please select or create a business plan to manage costs.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cost Management</h1>
          <p className="text-gray-600 mt-1">
            Comprehensive cost tracking with strategic alignment and category-specific management
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-sm">
            <Building2 className="h-3 w-3 mr-1" />
            {currentPlan.company_name}
          </Badge>
          <Badge variant="outline" className="text-sm">
            {costItems.length} Cost Items
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Annual Costs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(costItems.reduce((sum, item) => {
                    let annualAmount = 0;
                    if (item.cost_type === 'monthly') annualAmount = item.amount * 12;
                    else if (item.cost_type === 'annual') annualAmount = item.amount;
                    
                    // Convert to display currency
                    const itemCurrency = item.currency || currentPlan?.currency || 'USD';
                    const targetCurrency = displayCurrency || currentPlan?.currency || 'USD';
                    const converted = convertCurrency(annualAmount, itemCurrency, targetCurrency);
                    
                    return sum + converted;
                  }, 0), displayCurrency || currentPlan?.currency || 'USD')}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cost Categories</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(costItems.map(c => c.cost_category)).size}
                </p>
              </div>
              <PieChart className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Strategically Linked</p>
                <p className="text-2xl font-bold text-gray-900">
                  {costItems.filter(c => c.linked_objective_id).length}
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">With Growth Rates</p>
                <p className="text-2xl font-bold text-gray-900">
                  {costItems.filter(c => c.annual_growth_rate || c.growth_rates_per_year).length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="core-costs">
            <DollarSign className="h-4 w-4 mr-2" />
            Core Costs
          </TabsTrigger>
          <TabsTrigger value="operations">
            <Building2 className="h-4 w-4 mr-2" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="investments">
            <TrendingUp className="h-4 w-4 mr-2" />
            Investments
          </TabsTrigger>
          <TabsTrigger value="financial">
            <CreditCard className="h-4 w-4 mr-2" />
            Financial
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Core Costs */}
        <TabsContent value="core-costs" className="space-y-6">
          <CoreCostsTab 
            costItems={costItems}
            costLibraries={costLibraries}
            onAddCost={openCostDialog}
            onEdit={handleEditCost}
            onDelete={handleDeleteCost}
          />
        </TabsContent>

        {/* Tab 2: Operations & Production */}
        <TabsContent value="operations" className="space-y-6">
          <OperationsTab 
            costItems={costItems}
            costLibraries={costLibraries}
            products={products}
            onAddCost={openCostDialog}
            onEdit={handleEditCost}
            onDelete={handleDeleteCost}
          />
        </TabsContent>

        {/* Tab 3: Investments & Assets */}
        <TabsContent value="investments" className="space-y-6">
          <InvestmentsTab 
            costItems={costItems}
            costLibraries={costLibraries}
            onAddCost={openCostDialog}
            onEdit={handleEditCost}
            onDelete={handleDeleteCost}
          />
        </TabsContent>

        {/* Tab 4: Financial & Compliance */}
        <TabsContent value="financial" className="space-y-6">
          <FinancialComplianceTab 
            costItems={costItems}
            costLibraries={costLibraries}
            financialLoans={financialLoans}
            onAddCost={openCostDialog}
            onEdit={handleEditCost}
            onDelete={handleDeleteCost}
          />
        </TabsContent>
      </Tabs>

      {/* Cost Form Dialog */}
      <Dialog open={showCostDialog} onOpenChange={setShowCostDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCost ? 'Edit' : 'Add'} {getCategoryName(selectedCategory)}
            </DialogTitle>
            <DialogDescription>
              {getCategoryDescription(selectedCategory)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCost} className="space-y-6">
            <CostFormContent
              form={costForm}
              setForm={setCostForm}
              category={selectedCategory}
              costLibraries={costLibraries}
              sfHierarchy={sfHierarchy}
              financialLoans={financialLoans}
              products={products}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCostDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingCost ? 'Update' : 'Create'} Cost Item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper functions
const getCategoryName = (category) => {
  const names = {
    'fixed': 'Fixed Cost',
    'variable': 'Variable Cost',
    'semi_variable': 'Semi-Variable Cost',
    'direct': 'Direct Cost',
    'indirect': 'Indirect Cost',
    'opex': 'Operating Expense',
    'capex': 'Capital Expenditure',
    'financial': 'Financial Cost',
    'taxes': 'Tax Item'
  };
  return names[category] || 'Cost Item';
};

const getCategoryDescription = (category) => {
  const descriptions = {
    'fixed': 'Costs that don\'t change with production volume',
    'variable': 'Costs that vary directly with production or sales',
    'semi_variable': 'Costs with both fixed and variable components',
    'direct': 'Costs directly traceable to specific products',
    'indirect': 'Shared costs across multiple products',
    'opex': 'Operating expenses for running the business',
    'capex': 'Long-term asset investments',
    'financial': 'Interest and financing costs',
    'taxes': 'Tax obligations and planning'
  };
  return descriptions[category] || '';
};

// Placeholder Tab Components (will be built in Phase C.3)
const CoreCostsTab = ({ costItems, costLibraries, onAddCost, onEdit, onDelete }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Fixed Costs</CardTitle>
            <CardDescription>Costs that remain constant regardless of production volume</CardDescription>
          </div>
          <Button onClick={() => onAddCost('fixed')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Fixed Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'fixed')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Variable Costs</CardTitle>
            <CardDescription>Costs that change with production or sales volume</CardDescription>
          </div>
          <Button onClick={() => onAddCost('variable')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Variable Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'variable')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Semi-Variable Costs</CardTitle>
            <CardDescription>Costs with both fixed and variable components</CardDescription>
          </div>
          <Button onClick={() => onAddCost('semi_variable')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Semi-Variable Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'semi_variable')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>
  </div>
);

const OperationsTab = ({ costItems, costLibraries, products, onAddCost, onEdit, onDelete }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Operating Expenses</CardTitle>
            <CardDescription>Day-to-day operational and administrative expenses</CardDescription>
          </div>
          <Button onClick={() => onAddCost('opex')}>
            <Plus className="h-4 w-4 mr-2" />
            Add OpEx
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'opex')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Direct Costs</CardTitle>
            <CardDescription>Costs directly traceable to specific products</CardDescription>
          </div>
          <Button onClick={() => onAddCost('direct')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Direct Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'direct')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Indirect Costs</CardTitle>
            <CardDescription>Shared costs allocated across multiple products</CardDescription>
          </div>
          <Button onClick={() => onAddCost('indirect')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Indirect Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'indirect')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>
  </div>
);

const InvestmentsTab = ({ costItems, costLibraries, onAddCost, onEdit, onDelete }) => (
  <Card>
    <CardHeader>
      <div className="flex justify-between items-center">
        <div>
          <CardTitle>Capital Expenditures</CardTitle>
          <CardDescription>Long-term asset investments and acquisitions</CardDescription>
        </div>
        <Button onClick={() => onAddCost('capex')}>
          <Plus className="h-4 w-4 mr-2" />
          Add CapEx
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <CostItemsTable 
        items={costItems.filter(c => c.cost_category === 'capex')}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </CardContent>
  </Card>
);

const FinancialComplianceTab = ({ costItems, costLibraries, financialLoans, onAddCost, onEdit, onDelete }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Financial Costs</CardTitle>
            <CardDescription>Interest, fees, and financing-related expenses</CardDescription>
          </div>
          <Button onClick={() => onAddCost('financial')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Financial Cost
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {financialLoans.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>{financialLoans.length} loans</strong> available from Financial Management for linking
            </p>
          </div>
        )}
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'financial')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Tax Planning</CardTitle>
            <CardDescription>Tax obligations and compliance costs</CardDescription>
          </div>
          <Button onClick={() => onAddCost('taxes')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Tax Item
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <CostItemsTable 
          items={costItems.filter(c => c.cost_category === 'taxes')}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CardContent>
    </Card>
  </div>
);

// Reusable Cost Items Table
const CostItemsTable = ({ items, onEdit, onDelete }) => {
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const { currentPlan } = useBusinessPlan();
  const [calculatedAmounts, setCalculatedAmounts] = useState({});
  
  const formatCurrency = (amount, sourceCurrency = null) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    const fromCurrency = sourceCurrency || currentPlan?.currency || 'USD';
    return formatCurrencyContext(amount, fromCurrency, null, false);
  };
  
  // Fetch calculated amounts for per_unit and percentage_of_revenue costs
  useEffect(() => {
    const fetchCalculatedAmounts = async () => {
      const itemsNeedingCalculation = items.filter(item => 
        (item.cost_type === 'per_unit' || item.cost_type === 'percentage_of_revenue') &&
        item.linked_product_id &&
        (!item.calculated_amount || item.calculated_amount === null)
      );
      
      for (const item of itemsNeedingCalculation) {
        try {
          const response = await axios.get(
            `${API}/v2/products-enhanced/product-planning/${item.linked_product_id}`
          );
          
          const productData = response.data;
          let calculated = 0;
          
          if (item.cost_type === 'per_unit') {
            calculated = item.amount * (productData.ytd_planned_units || 0);
          } else if (item.cost_type === 'percentage_of_revenue') {
            calculated = (productData.ytd_planned_revenue || 0) * (item.amount / 100);
          }
          
          setCalculatedAmounts(prev => ({
            ...prev,
            [item.id]: calculated
          }));
        } catch (error) {
          console.error(`Error calculating amount for ${item.id}:`, error);
        }
      }
    };
    
    if (items.length > 0) {
      fetchCalculatedAmounts();
    }
  }, [items]);
  
  // Helper function to get display amount for per_unit and percentage_of_revenue costs
  const getDisplayAmount = (item) => {
    // For per_unit and percentage_of_revenue, show CALCULATED amount if available
    if (item.cost_type === 'per_unit') {
      const calculatedAmount = item.calculated_amount ?? calculatedAmounts[item.id];
      
      if (calculatedAmount !== null && calculatedAmount !== undefined) {
        // Show the calculated total amount
        return (
          <div>
            <div className="font-semibold text-gray-900">{formatCurrency(calculatedAmount, item.currency)}</div>
            <div className="text-xs text-gray-500">{formatCurrency(item.amount, item.currency)} per unit × YTD units</div>
            {item.linked_product_id && (
              <div className="text-xs text-blue-600 font-medium mt-1">
                📊 Auto-calculated from product
              </div>
            )}
          </div>
        );
      }
      // Fallback if no calculated amount
      return (
        <div>
          <div className="font-semibold text-gray-900">{formatCurrency(item.amount, item.currency)}</div>
          <div className="text-xs text-gray-500">per unit produced</div>
        </div>
      );
    }
    
    if (item.cost_type === 'percentage_of_revenue') {
      const calculatedAmount = item.calculated_amount ?? calculatedAmounts[item.id];
      
      if (calculatedAmount !== null && calculatedAmount !== undefined) {
        // Show the calculated total amount
        return (
          <div>
            <div className="font-semibold text-gray-900">{formatCurrency(calculatedAmount, item.currency)}</div>
            <div className="text-xs text-gray-500">{item.amount}% of YTD revenue</div>
            {item.linked_product_id && (
              <div className="text-xs text-blue-600 font-medium mt-1">
                📊 Auto-calculated from product
              </div>
            )}
          </div>
        );
      }
      // Fallback if no calculated amount
      return (
        <div>
          <div className="font-semibold text-gray-900">{item.amount}%</div>
          <div className="text-xs text-gray-500">of product revenue</div>
        </div>
      );
    }
    
    // For all other types, show the amount normally
    return (
      <div>
        <div className="font-semibold text-gray-900">
          {formatCurrency(item.amount, item.currency)}
        </div>
        {item.cost_type === 'monthly' && <div className="text-xs text-gray-500">per month</div>}
        {item.cost_type === 'annual' && <div className="text-xs text-gray-500">per year</div>}
        {item.cost_type === 'one_time' && <div className="text-xs text-gray-500">one-time</div>}
      </div>
    );
  };
  
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
        <p>No cost items in this category yet</p>
        <p className="text-sm">Click the button above to add your first item</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SF Link</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900">{item.item_name}</p>
                  {item.description && (
                    <p className="text-xs text-gray-500">{item.description}</p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-gray-900">
                {getDisplayAmount(item)}
              </td>
              <td className="px-4 py-3">
                <Badge variant="outline" className="text-xs">
                  {item.cost_type.replace('_', ' ')}
                </Badge>
              </td>
              <td className="px-4 py-3">
                {item.linked_objective_id ? (
                  <Badge variant="secondary" className="text-xs">
                    <Target className="h-3 w-3 mr-1" />
                    Linked
                  </Badge>
                ) : (
                  <span className="text-xs text-gray-400">Not linked</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(item)}>
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CostManagementV2;
